import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useCsImportStatus } from '../../hooks/useCsInquiry.js';
import { formatNumber } from '../../lib/data-catalog-format.js';
import { StatusBanner } from '../StatusBanner.js';
import type { CsImportStatusResponse } from '../../types/cs-inquiry.js';

/**
 * 帯を出す画面と、その画面で件数のあとに添える一言（i18n キー）。
 * 画面ごとに「未取得があると何がずれるか」が違うので、一言だけを差し替える。
 * @graph-connects none
 */
const NOTE_KEYS = {
  dashboard: 'csImportStatus.dashboardNote',
  chats: 'csImportStatus.chatsNote',
  aiAnalysis: 'csImportStatus.aiAnalysisNote',
} as const;

/**
 * チャットの取り込みに問題があるときだけ出す警告の帯。
 *
 * - チャット台帳の取得が止まっている（判定は API）ときは、止まっている時間と最後に状態を取れた取得の時刻を出す。
 *   直近の取得記録に状態を取れた回が無いときは、取り込めていない時間を出す。一部の状態だけが取れ続けていない
 *   ときは、その時間と全状態を取れた最後の時刻を出す
 * - 状態を取り込めていないチャットがあるときは、その件数（期間に関係ない総数。最初の発言から API が返す猶予時間
 *   以上たったもの）と画面ごとの一言を出す。猶予時間を返し、取り込みが止まっていない（stopped でない）ときは、
 *   それより新しいチャットも取り込み待ちである旨を一言に足す。猶予時間を返さない古い API のときは猶予に触れない文言を出す
 *
 * どちらも無いとき・取得中・取得に失敗したときは何も出さない
 * （画面本体の読み込みや失敗表示とは独立した補助の知らせのため）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット台帳の取得が止まっていればその時間と最終取得時刻を、状態を取り込めていないチャットがあればその件数を、問い合わせ分析の各画面に警告の帯として出す
 * @graph-connects useCsImportStatus [calls] 取り込み状況の取得
 * @graph-connects formatNumber [calls] 件数の桁区切り
 * @graph-connects StatusBanner [calls] 帯の見た目
 * @graph-connects ledgerStallMessage [calls] 取り込みの問題の文言
 */
export function UnfetchedChatsBanner({ page }: { page: keyof typeof NOTE_KEYS }) {
  const { t } = useTranslation();
  const { data } = useCsImportStatus();

  if (data === undefined) return null;
  const { ledgerStall, unfetchedChats, unfetchedGraceHours, stallCheck } = data;
  if (ledgerStall == null && unfetchedChats === 0) return null;

  const note = t(NOTE_KEYS[page]);
  return (
    <>
      {ledgerStall != null && (
        <StatusBanner tone="warning" message={ledgerStallMessage(t, ledgerStall)} />
      )}
      {unfetchedChats > 0 && (
        <StatusBanner
          tone="warning"
          message={
            unfetchedGraceHours === undefined
              ? t('csImportStatus.unfetchedLead', { num: formatNumber(unfetchedChats) })
              : t('csImportStatus.unfetchedLeadWithGrace', {
                  num: formatNumber(unfetchedChats),
                  hours: formatNumber(unfetchedGraceHours),
                })
          }
          emphasis={
            // 取り込みが止まっている間・取り込みを確かめられなかったときは「次の取り込みで反映される」と言えないので、
            // 猶予の一言を足さない（一部の状態だけが取れていない partial のときは、取り込み自体は続いているので足す）
            unfetchedGraceHours === undefined ||
            ledgerStall?.reason === 'stopped' ||
            stallCheck === 'unavailable'
              ? note
              : t('csImportStatus.noteWithGrace', {
                  note,
                  grace: t('csImportStatus.graceNote', {
                    hours: formatNumber(unfetchedGraceHours),
                  }),
                })
          }
        />
      )}
    </>
  );
}

/**
 * チャット台帳の取り込みの問題（止まっている／一部の状態が取れ続けていない）を帯の文言にする。
 * @graph-connects formatNumber [calls] 時間の桁区切り
 */
function ledgerStallMessage(
  t: TFunction,
  stall: NonNullable<CsImportStatusResponse['ledgerStall']>,
): string {
  // 経過が分からない（記録が期間の外にしか無い）ときは時間を作らず、期間の日数以上・最後は不明と出す
  if (stall.hours === null) {
    return stall.reason === 'partial'
      ? t('csImportStatus.ledgerPartialBeyondWindow', { days: formatNumber(stall.windowDays) })
      : t('csImportStatus.ledgerStoppedBeyondWindow', { days: formatNumber(stall.windowDays) });
  }
  const hours = formatNumber(stall.hours);
  if (stall.reason === 'partial') {
    return stall.lastSuccessJst === null
      ? t('csImportStatus.ledgerPartialNoSuccess', { hours })
      : t('csImportStatus.ledgerPartial', { hours, at: stall.lastSuccessJst });
  }
  return stall.lastFetchedJst === null
    ? t('csImportStatus.ledgerNoRecentSuccess', { hours })
    : t('csImportStatus.ledgerStalled', { hours, at: stall.lastFetchedJst });
}
